import React, { useRef, useState } from 'react';
import { useApi } from '../apiHelpers';
import { Button } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import { PasswordStrength } from '../components/ui/DialogPassword';
import { Field } from '../components/ui/Field';
import Reveal from '../components/ui/Reveal';
import Tooltip from '../components/ui/Tooltip';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

type Which = 'current' | 'new' | 'confirm';

const MIN_LENGTH = 6;

const LABEL = 'mb-1.5 block text-[13px] font-medium leading-[18px] text-on-surface-variant';

const PasswordModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const api = useApi();

  // The parent mounts this only while it is open, so it closes itself and reports once its exit has run.
  const [open, setOpen] = useState(true);
  const after = useRef<() => void>(onClose);
  const leave = (then: () => void) => { after.current = then; setOpen(false); };

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorAt, setErrorAt] = useState<Which | null>(null);

  const fail = (at: Which, message: string) => { setErrorAt(at); setError(message); };
  const clear = () => { setError(''); setErrorAt(null); };

  const submit = async () => {
    clear();
    if (!currentPassword) { fail('current', 'Enter your current password.'); return; }
    if (!newPassword) { fail('new', 'Enter a new password.'); return; }
    if (newPassword.length < MIN_LENGTH) { fail('new', `The new password needs at least ${MIN_LENGTH} characters.`); return; }
    if (newPassword !== confirmPassword) { fail('confirm', 'The two new passwords differ. Type it again.'); return; }
    if (currentPassword === newPassword) { fail('new', 'The new password matches the current one. Choose a different one.'); return; }

    setSaving(true);
    try {
      await api.post('/users/me/password', { body: { currentPassword, newPassword } });
      leave(onSuccess);
    } catch (err: any) {
      console.error('Error changing password:', err);
      const msg: string = err?.message || 'The password did not change. Try again.';
      if (msg.includes('incorrect') || msg.includes('Invalid')) fail('current', 'The current password is not right. Enter it again.');
      else { setErrorAt(null); setError(msg); }
    } finally {
      setSaving(false);
    }
  };

  const mismatched = !!confirmPassword && newPassword !== confirmPassword;

  return (
    <Dialog
      open={open}
      onClose={() => leave(onClose)}
      onExited={() => after.current()}
      title="Change password"
      size="sm"
      busy={saving}
      dirty={!!(currentPassword || newPassword || confirmPassword)}
      dismissible={false}
      closeHint="Close without changing your password"
      onSubmit={submit}
      problem={error || null}
      footer={(
        <>
          <Button variant="tonal" disabled={saving} onClick={() => leave(onClose)}>Cancel</Button>
          <Tooltip label="Sign in with the new password from now on">
            <Button type="submit" variant="filled" disabled={saving}>{saving ? 'Changing' : 'Change password'}</Button>
          </Tooltip>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="pw-current" className={LABEL}>Current password</label>
          <Field
            id="pw-current"
            type="password"
            autoFocus
            autoComplete="current-password"
            value={currentPassword}
            error={errorAt === 'current'}
            onChange={(e) => { setCurrentPassword(e.target.value); clear(); }}
          />
        </div>

        <div>
          <label htmlFor="pw-new" className={LABEL}>New password</label>
          <Field
            id="pw-new"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            error={errorAt === 'new'}
            onChange={(e) => { setNewPassword(e.target.value); clear(); }}
          />
          <Reveal show={!!newPassword}>
            <PasswordStrength password={newPassword} hint={`At least ${MIN_LENGTH} characters`} />
          </Reveal>
        </div>

        <div>
          <label htmlFor="pw-confirm" className={LABEL}>New password again</label>
          <Field
            id="pw-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            error={mismatched || errorAt === 'confirm'}
            aria-describedby="pw-confirm-note"
            onChange={(e) => { setConfirmPassword(e.target.value); clear(); }}
          />
          <Reveal show={mismatched}>
            <p id="pw-confirm-note" className="m-0 mt-1.5 text-xs text-red-300">The two new passwords differ.</p>
          </Reveal>
        </div>
      </div>
    </Dialog>
  );
};

export default PasswordModal;
